import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Route, Search } from "lucide-react";
import ReactSelect, { SingleValue } from "react-select";
import PageHeader from "../../components/PageHeader";
import { SELECT_STYLES } from "../../components/DateFilters/constants";
import FilaEntregadores from "../Roterista/components/FilaEntregadores";
import ChamarProximoButton from "../Roterista/components/ChamarProximoButton";
import ModalChamada from "../Roterista/components/ModalChamada";
import ModalBuscarPedido from "../Roterista/components/ModalBuscarPedido";
import EntregadoresEmEntrega from "../Roterista/components/EntregadoresEmEntrega";
import EntregasRealizadasHoje from "../Roterista/components/EntregasRealizadasHoje";
import {
  RoteristaAutomaticoProvider,
  useRoteristaAutomatico,
} from "./context/RoteristaAutomaticoContext";
import CapacidadeBanner from "./components/CapacidadeBanner";
import LotesSugeridos from "./components/LotesSugeridos";
import PedidosSemLocalizacaoList from "./components/PedidosSemLocalizacao";

const RoteristaAutomaticoContent: React.FC = () => {
  const [isModalBuscarPedidoOpen, setIsModalBuscarPedidoOpen] = useState(false);
  const {
    lojaId,
    lojas,
    selectedLojaId,
    setSelectedLojaId,
    showLojaSelector,
    isLoading,
    lotesData,
    selectedLoteId,
    setSelectedLoteId,
    selectedLote,
    entregadoresNaFila,
    entregadoresEmEntrega,
    proximoChamado,
    enderecos,
    lockedDisplayIds,
    isChamando,
    isConfirmandoSaida,
    usaIntegracaoPedidos,
    handleChamarProximo,
    handleChamarEntregador,
    handleConfirmarSaida,
    handleMoverParaFim,
    handleChamarNovamente,
    handleCloseModal,
    atualizarPedidosEntrega,
    handleFinalizarEntrega,
    finalizandoEntregaId,
    refreshLive,
  } = useRoteristaAutomatico();

  const lojaOptions = useMemo(
    () => lojas.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojas]
  );
  const selectedLojaOption =
    lojaOptions.find((o) => o.value === selectedLojaId) ?? null;

  const canChamar =
    Boolean(selectedLote) &&
    selectedLote?.liberadoParaSaida !== false &&
    entregadoresNaFila.length > 0 &&
    !isChamando &&
    Boolean(lojaId);

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {proximoChamado && (
        <ModalChamada
          isOpen={!!proximoChamado}
          onClose={handleCloseModal}
          entregador={proximoChamado}
          enderecos={enderecos}
          onConfirmar={(displayIds) => void handleConfirmarSaida(displayIds)}
          onMoverFim={() => void handleMoverParaFim()}
          onChamarNovamente={() => void handleChamarNovamente()}
          usaIntegracaoPedidos={usaIntegracaoPedidos}
          lockedDisplayIds={lockedDisplayIds}
          isConfirmando={isConfirmandoSaida}
        />
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <PageHeader
          title="Roteirista Automático"
          description="O sistema define o lote; você só chama e confirma o motoboy"
        />
        <Link
          to="/roterista"
          className="inline-flex items-center gap-2 self-start rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
        >
          <Route size={16} />
          Ir ao Roteirista manual
        </Link>
      </div>

      {showLojaSelector && lojas.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <label
            htmlFor="roterista-auto-loja"
            className="text-sm font-medium text-neutral-700"
          >
            Loja:
          </label>
          <ReactSelect<{ value: number; label: string }>
            inputId="roterista-auto-loja"
            value={selectedLojaOption}
            onChange={(
              option: SingleValue<{ value: number; label: string }>
            ) => setSelectedLojaId(option?.value ?? undefined)}
            options={lojaOptions}
            isClearable
            placeholder="Selecione uma loja"
            className="w-full min-w-[140px] sm:w-56"
            styles={SELECT_STYLES}
          />
        </div>
      )}

      {showLojaSelector && !lojaId && (
        <p className="mb-4 rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-800">
          Selecione uma loja para ver os lotes.
        </p>
      )}

      {isLoading ? (
        <div className="flex flex-col items-center gap-2 py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          <p className="text-neutral-500">Carregando lotes...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <CapacidadeBanner data={lotesData} />

          <div className="flex flex-wrap items-center gap-3">
            <ChamarProximoButton
              disabled={!canChamar}
              loading={isChamando}
              onClick={() => void handleChamarProximo()}
            />

            <button
              type="button"
              onClick={() => setIsModalBuscarPedidoOpen(true)}
              className="flex items-center gap-2 rounded-lg border-2 border-primary/40 bg-primary-light/15 px-4 py-3 font-semibold text-primary hover:bg-primary-light/25"
            >
              <Search size={18} />
              Buscar pedido
            </button>

            <button
              type="button"
              onClick={refreshLive}
              className="flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-3 font-semibold text-neutral-700 hover:bg-neutral-50"
            >
              <RefreshCw size={18} />
              Atualizar
            </button>
          </div>

          {!canChamar && lojaId && (
            <p className="text-sm text-neutral-600">
              {!selectedLote
                ? "Aguardando lote sugerido (comanda pronta com localização)."
                : entregadoresNaFila.length === 0
                  ? "Não há entregador na fila de aguardando — por isso o botão fica desabilitado (igual ao Roteirista)."
                  : null}
            </p>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <LotesSugeridos
              lotes={lotesData?.lotes ?? []}
              selectedLoteId={selectedLoteId}
              onSelect={setSelectedLoteId}
            />
            <FilaEntregadores
              entregadores={entregadoresNaFila}
              onChamarEntregador={(id) => void handleChamarEntregador(id)}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <EntregadoresEmEntrega
              entregadores={entregadoresEmEntrega}
              onAtualizarPedidos={atualizarPedidosEntrega}
              onFinalizarEntrega={handleFinalizarEntrega}
              finalizandoEntregaId={finalizandoEntregaId}
            />
            <EntregasRealizadasHoje lojaId={lojaId} />
          </div>

          <PedidosSemLocalizacaoList
            items={lotesData?.semLocalizacao ?? []}
          />
        </div>
      )}

      <ModalBuscarPedido
        isOpen={isModalBuscarPedidoOpen}
        onClose={() => setIsModalBuscarPedidoOpen(false)}
        lojaId={lojaId}
      />
    </main>
  );
};

const RoteristaAutomatico: React.FC = () => {
  return (
    <RoteristaAutomaticoProvider>
      <RoteristaAutomaticoContent />
    </RoteristaAutomaticoProvider>
  );
};

export default RoteristaAutomatico;
